import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const fmtNum = (n) => new Intl.NumberFormat("nl-NL").format(Math.round(Number(n) || 0));

function trendRichting(data) {
  if (!data || data.length < 2) return "flat";
  const d = data[data.length - 1].trend - data[0].trend;
  if (d > 0.5) return "up";
  if (d < -0.5) return "down";
  return "flat";
}

export function ProductTrends({ orgId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/organisaties/${orgId}/producttrends`)
      .then((r) => setData(r.data))
      .catch(() => setData({ producten: [], jaren: [] }))
      .finally(() => setLoading(false));
  }, [orgId]);

  if (loading) return <div className="text-sm text-muted-foreground">Laden…</div>;
  if (!data?.producten?.length)
    return <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">Nog geen verkoopdata voor deze klant.</div>;

  return (
    <div data-testid="producttrends">
      <p className="text-sm text-muted-foreground mb-4">
        Verkochte stuks per jaar ({data.jaren[0]}–{data.jaren[data.jaren.length - 1]}) per product, met trendlijn.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.producten.map((p) => {
          const richting = trendRichting(p.data);
          const TrendIcon = richting === "up" ? TrendingUp : richting === "down" ? TrendingDown : Minus;
          const tone = richting === "up" ? "text-emerald-500" : richting === "down" ? "text-destructive" : "text-muted-foreground";
          return (
            <div key={p.code} className="bg-card border border-border rounded-xl p-4" data-testid={`trend-${p.code}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{p.naam}</div>
                  <div className="text-xs text-muted-foreground font-mono">{p.code}</div>
                </div>
                <div className={`flex items-center gap-1 text-xs shrink-0 ${tone}`}>
                  <TrendIcon className="h-4 w-4" />
                  <span>{fmtNum(p.totaal)} st.</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <ComposedChart data={p.data} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="jaar" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} width={44} tickFormatter={fmtNum} />
                  <Tooltip
                    formatter={(v, n) => [fmtNum(v), n === "trend" ? "Trend" : "Stuks"]}
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="aantal" name="Stuks" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={42} />
                  <Line dataKey="trend" name="Trend" stroke="#f59e0b" strokeWidth={2.5} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          );
        })}
      </div>
    </div>
  );
}
